import {
  CardGiftcard,
  EditOff,
  History,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  PersonAdd,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import {
  AnsweringClient,
  LicenseEventDTO,
} from "../../../clients/answeringClient";
import {
  IResellerClient,
  ResellerManagementController,
} from "../../controllers/ResellerManagementController";
import { getOriginLabel } from "../../utils/labels";
import { LicenseEventsModal } from "../modals/LicenseEventsModal";
import { RevokeLicenseModal } from "../modals/RevokeLicenseModal";
import { trackGtagEvent } from "../../../utils/analytics";

export interface ILicenseHistory {
  id: number;
  resellerId: number;
  clientId: number;
  instrumentId: number;
  quantityAssigned: number;
  quantityConsumed: number;
  assignedAt: Date;
  expiresAt: Date | null;
  clientName: string;
  instrumentName: string;
  unfinishedCount: number;
}

interface Props {
  client: IResellerClient;
  onPromoteClick: (client: IResellerClient) => void;
  onAssignLicenseClick: (client: IResellerClient) => void;
}

interface State {
  isExpanded: boolean;
  isLoadingLicenses: boolean;
  licenses: ILicenseHistory[];
  anchorEl: HTMLElement | null;
  isRevokeModalOpen: boolean;
  licenseToRevoke: ILicenseHistory | null;

  isEventsModalOpen: boolean;
  licenseEvents: LicenseEventDTO[];
  isLoadingEvents: boolean;
}

class ResellerClientRow extends React.Component<Props, State> {
  private answeringClient = new AnsweringClient();
  private controller = new ResellerManagementController();

  constructor(props: Props) {
    super(props);
    this.state = {
      isExpanded: false,
      isLoadingLicenses: false,
      licenses: [],
      anchorEl: null,
      isRevokeModalOpen: false,
      licenseToRevoke: null,

      isEventsModalOpen: false,
      licenseEvents: [],
      isLoadingEvents: false,
    };
  }

  handleExpandClick = () => {
    const { isExpanded, licenses } = this.state;
    const shouldFetch = !isExpanded && licenses.length === 0;
    this.setState({ isExpanded: !isExpanded }, () => {
      if (shouldFetch) {
        this.fetchLicenses();
      }
    });
  };

  fetchLicenses = () => {
    this.setState({ isLoadingLicenses: true });
    const token = localStorage.getItem("AccessToken")!;
    this.answeringClient
      .getResellerLicenseHistory(token, {
        clientId: this.props.client.clientId,
        limit: 100,
      })
      .then((response) => {
        this.setState({
          licenses: response.data.data,
          isLoadingLicenses: false,
        });
      })
      .catch((error) => {
        console.error("Failed to fetch licenses for client:", error);
        this.setState({ isLoadingLicenses: false });
      });
  };

  openMenu = (e: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: e.currentTarget });
  };
  closeMenu = () => {
    this.setState({ anchorEl: null });
  };
  handleAssign = () => {
    this.props.onAssignLicenseClick(this.props.client);
    this.closeMenu();
  };
  handlePromote = () => {
    this.props.onPromoteClick(this.props.client);
    this.closeMenu();
  };

  handleOpenRevokeModal = (license: ILicenseHistory) => {
    this.setState({ isRevokeModalOpen: true, licenseToRevoke: license });
  };

  handleCloseRevokeModal = () => {
    this.setState({ isRevokeModalOpen: false, licenseToRevoke: null });
  };

  handleConfirmRevoke = (quantityToRevoke: number) => {
    this.setState({ isLoadingLicenses: true });
    const token = localStorage.getItem("AccessToken")!;
    const license = this.state.licenseToRevoke!;
    const licenseId = license.id;

    this.controller
      .revokeResellerLicenses(token, licenseId, quantityToRevoke)
      .then(() => {
        this.trackResellerInstanceRevoked(
          this.props.client.clientUid,
          license.instrumentId,
          quantityToRevoke
        );
        this.handleCloseRevokeModal();
        this.fetchLicenses();
      })
      .catch((err) => {
        console.error("Failed to revoke licenses", err);
        this.setState({ isLoadingLicenses: false });
      });
  };

  handleOpenEventsModal = () => {
    this.setState({ isEventsModalOpen: true, isLoadingEvents: true });

    const token = localStorage.getItem("AccessToken")!;
    this.answeringClient
      .getResellerLicenseEvents(token, {
        clientId: this.props.client.clientId,
        limit: 100,
      })
      .then((res) => {
        this.setState({
          licenseEvents: res.data.data,
          isLoadingEvents: false,
        });
      })
      .catch((err) => {
        console.error("Erro ao carregar eventos da licença", err);
        this.setState({ isLoadingEvents: false });
      });
  };

  handleCloseEventsModal = () => {
    this.setState({ isEventsModalOpen: false, licenseEvents: [] });
  };

  private trackResellerInstanceRevoked(
    clientUid: string,
    instrumentId: number,
    quantity: number
  ) {
    if (
      !clientUid ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const fbq = window.fbq;
    const payload = {
      test_id: instrumentId,
      quantity,
      assigned_user_id: clientUid,
    };

    if (fbq) {
      fbq("trackCustom", "ResellerInstanceRevoked", payload);
    }

    trackGtagEvent("reseller_instance_revoked", payload);
  }

  private renderInvitationStatus = (client: IResellerClient) => {
    if (client.origin === "EXISTING_USER_LINKED" || !client.origin)
      return <Typography variant="body2">—</Typography>;

    const status =
      client.invitationStatus ?? (client.acceptedAt ? "ACCEPTED" : "PENDING");

    if (status === "ACCEPTED")
      return <Chip size="small" label="Aceito" color="success" />;
    return <Chip size="small" label="Pendente" color="warning" />;
  };

  private renderOrigin = (client: IResellerClient) => {
    const label = getOriginLabel(client.origin);
    const color: "default" | "info" =
      client.origin === "INVITED_NEW_USER" ? "info" : "default";
    return <Chip size="small" label={label} color={color} />;
  };

  render() {
    const { client } = this.props;
    const { isExpanded, isLoadingLicenses, licenses, anchorEl } = this.state;
    const isPsychologist = client.isPsychologist;
    const menuOpen = Boolean(anchorEl);

    return (
      <React.Fragment>
        <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={this.handleExpandClick}
            >
              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {client.clientName}
          </TableCell>
          <TableCell>{client.clientEmail}</TableCell>
          <TableCell>
            {client.linkedAt.toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
            })}
          </TableCell>
          <TableCell>{this.renderOrigin(client)}</TableCell>
          <TableCell>{this.renderInvitationStatus(client)}</TableCell>
          <TableCell align="right">
            <IconButton
              aria-label="actions"
              size="medium"
              sx={{
                backgroundColor: "grey.200",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "grey.300",
                },
              }} onClick={this.openMenu}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={this.closeMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={this.handleAssign}>
                <CardGiftcard fontSize="small" sx={{ mr: 1 }} />
                Distribuir Licença
              </MenuItem>

              {isPsychologist ? (
                <Tooltip
                  title="Este cliente já é um psicólogo"
                  placement="left"
                >
                  <div>
                    <MenuItem disabled>
                      <PersonAdd fontSize="small" sx={{ mr: 1 }} />
                      Promover a Psicólogo
                    </MenuItem>
                  </div>
                </Tooltip>
              ) : (
                <MenuItem onClick={this.handlePromote}>
                  <PersonAdd fontSize="small" sx={{ mr: 1 }} />
                  Promover a Psicólogo
                </MenuItem>
              )}
            </Menu>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  margin: 1,
                  padding: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Histórico de Licenças
                </Typography>
                {isLoadingLicenses ? (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress size={24} />
                  </Box>
                ) : licenses.length === 0 ? (
                  <Typography variant="body2">
                    Nenhuma licença distribuída para este cliente.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Instrumento</TableCell>
                        <TableCell>Qtd. Distribuída</TableCell>
                        <TableCell>Qtd. Consumida</TableCell>
                        <TableCell>Aplicações não finalizadas</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {licenses.map((license) => {
                        const canRevoke =
                          license.quantityAssigned > license.quantityConsumed;
                        return (
                          <TableRow key={license.id}>
                            <TableCell>{license.instrumentName}</TableCell>
                            <TableCell>{license.quantityAssigned}</TableCell>
                            <TableCell>{license.quantityConsumed}</TableCell>
                            <TableCell>{license.unfinishedCount}</TableCell>
                            <TableCell align="right"
                              sx={{
                                display: "flex",
                              }}>
                              <Tooltip title="Ver movimentações da licença">
                                <IconButton
                                  size="small"
                                  onClick={() => this.handleOpenEventsModal()}
                                >
                                  <History fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip
                                title={
                                  canRevoke
                                    ? "Revogar licenças não utilizadas"
                                    : "Todas as licenças foram consumidas"
                                }
                              >
                                <div>
                                  <IconButton
                                    size="small"
                                    disabled={!canRevoke}
                                    onClick={() =>
                                      this.handleOpenRevokeModal(license)
                                    }
                                  >
                                    <EditOff fontSize="small" />
                                  </IconButton>
                                </div>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
        {this.state.licenseToRevoke && (
          <RevokeLicenseModal
            isOpen={this.state.isRevokeModalOpen}
            isLoading={this.state.isLoadingLicenses}
            license={this.state.licenseToRevoke}
            onClose={this.handleCloseRevokeModal}
            onConfirm={this.handleConfirmRevoke}
          />
        )}
        {this.state.isEventsModalOpen && (
          <LicenseEventsModal
            isOpen={this.state.isEventsModalOpen}
            onClose={this.handleCloseEventsModal}
            isLoading={this.state.isLoadingEvents}
            events={this.state.licenseEvents}
          />
        )}
      </React.Fragment>
    );
  }
}

export default ResellerClientRow;
